/**
 * Chunk 187: Yield Aggregator & DeFi Opportunity Scanner — API client
 */
import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StrategyType = 'lending' | 'liquidity' | 'liquid_staking' | 'vault';
export type RiskLevel = 'low' | 'medium' | 'high';

export interface YieldOpportunity {
  id: string;
  protocolSlug: string;
  protocolName: string;
  chain: string;
  assetSymbol: string;
  strategyType: StrategyType;
  apyBase: number;
  apyReward: number;
  apyTotal: number;
  tvlUsd: number | null;
  riskLevel: RiskLevel;
  lastUpdated: string;
  protocolUrl: string;
}

export interface YieldOpportunitiesResponse {
  opportunities: YieldOpportunity[];
  fetchedAt: string;
  total: number;
}

export interface YieldOpportunityFilters {
  asset?: string;
  chain?: string;
  strategyType?: StrategyType;
  minApy?: number;
}

export interface UserYieldOpportunity extends YieldOpportunity {
  currentProtocol: string | null;
  currentApy: number | null;
  apyDelta: number | null;
  isUpgradeOpportunity: boolean;
}

export interface UserYieldOpportunitiesResponse {
  userId: string;
  heldSymbols: string[];
  opportunities: UserYieldOpportunity[];
  upgradeOpportunityCount: number;
  fetchedAt: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function getYieldOpportunities(
  filters?: YieldOpportunityFilters,
): Promise<YieldOpportunitiesResponse> {
  const params: Record<string, string> = {};
  if (filters?.asset) params.asset = filters.asset;
  if (filters?.chain) params.chain = filters.chain;
  if (filters?.strategyType) params.strategyType = filters.strategyType;
  if (filters?.minApy !== undefined) params.minApy = String(filters.minApy);

  const res = await apiClient.get<YieldOpportunitiesResponse>('/analytics/yield-opportunities', {
    params,
  });
  return res.data;
}

export async function getUserYieldOpportunities(
  userId: string,
): Promise<UserYieldOpportunitiesResponse> {
  const res = await apiClient.get<UserYieldOpportunitiesResponse>(
    `/analytics/yield-opportunities/user/${userId}`,
  );
  return res.data;
}

// ─── Labels ───────────────────────────────────────────────────────────────────

export const STRATEGY_TYPE_LABELS: Record<StrategyType, string> = {
  lending: 'Lending',
  liquidity: 'Liquidity',
  liquid_staking: 'Liquid Staking',
  vault: 'Vault',
};

export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  low: 'Low Risk',
  medium: 'Medium Risk',
  high: 'High Risk',
};

export const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  bsc: 'BNB Chain',
  avalanche: 'Avalanche',
};
